<template>
  <div class="max-w-4xl w-full mx-auto space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">Snapshot Bulk Upload</h1>
    <div v-if="selected_university" class="mt-1">
      <a :href="snapshotPath" target="_blank" rel="noopener" class="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50">
        Open snapshot page ({{ selected_university.slug }})
      </a>
    </div>

    <!-- University selector -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Select University</label>
      <select v-model="selected_university" @change="onUniChange" class="w-full h-10 px-3 border rounded-lg">
        <option disabled :value="null">Select University</option>
        <option v-for="u in universities" :key="u.id" :value="u">{{ u.name }}</option>
      </select>
    </div>

    <!-- Ceremony selector -->
    <div v-if="selected_university">
      <label class="block text-sm font-medium text-gray-700 mb-1">Select Ceremony</label>
      <select v-model="selected_ceremony" class="w-full h-10 px-3 border rounded-lg">
        <option disabled :value="null">Select ceremony</option>
        <option v-for="c in ceremonies" :key="c.id" :value="c.id">
          {{ formatCeremonyOption(c) }}
        </option>
      </select>
    </div>

    <!-- Dropzone -->
    <div v-if="selected_ceremony">
      <div
        class="border-2 border-dashed rounded-lg p-8 text-center bg-white"
        @dragover.prevent
        @dragenter.prevent
        @drop.prevent="onDrop"
      >
        <p class="text-gray-700">Drag and drop images here, or</p>
        <input type="file" multiple accept="image/*" @change="onFileChange" class="mt-2" />
      </div>
    </div>

    <!-- Files table -->
    <div v-if="files.length">
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold">{{ files.length }} file(s) ready</h2>
        <button :disabled="uploading" @click="startUpload" class="px-4 py-2 rounded-md border bg-white hover:bg-gray-50">
          {{ uploading ? 'Uploading…' : 'Start Upload' }}
        </button>
      </div>
      <ul class="divide-y bg-white rounded-md border mt-2">
        <li v-for="f in files" :key="f.id" class="p-3 flex items-center justify-between">
          <div class="truncate">
            <div class="font-medium truncate">{{ f.file.name }}</div>
            <div class="text-xs text-gray-500">{{ (f.file.size/1024/1024).toFixed(2) }} MB</div>
          </div>
          <div class="w-1/2 ml-4">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-blue-600 h-2 rounded-full" :style="{ width: (f.progress||0) + '%' }"></div>
            </div>
            <div class="text-xs text-gray-500 mt-1">{{ f.status }}</div>
          </div>
        </li>
      </ul>
    </div>

    <div v-if="doneCount>0 && doneCount===files.length" class="p-3 rounded bg-green-50 text-green-700">
      <div class="flex items-center justify-between">
        <span>All uploads completed.</span>
        <a :href="snapshotPath" target="_blank" rel="noopener" class="underline font-medium">View snapshots page</a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
function decodeJwt(token) {
  try {
    const part = token.split('.')[1]
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decodeURIComponent(escape(json)))
  } catch (e) { return {} }
}

export default {
  name: 'SnapshotBulkUpload',
  data() {
    return {
      universities: [],
      selected_university: null,
      ceremonies: [],
      selected_ceremony: null,
      files: [],
      uploading: false,
      concurrency: 8,
      maxRetries: 3,
    }
  },
  computed: {
    doneCount() {
      return this.files.filter(f => f.status === 'done').length
    },
    snapshotPath() {
      return this.selected_university ? `/snapshot/${this.selected_university.slug}` : '#'
    }
  },
  created() {
    // Guard: redirect if user lacks snapshot-upload/admin
    const payload = decodeJwt(localStorage.getItem('token') || '')
    const roles = Array.isArray(payload?.roles) ? payload.roles : []
    const canSnapshot = roles.includes('snapshot-upload') || roles.includes('admin')
    if (!canSnapshot) {
      this.$router.replace('/admin/upload')
      return
    }
    // Tune concurrency based on connection (best-effort)
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if (conn && conn.effectiveType) {
        if (conn.effectiveType.includes('2g')) this.concurrency = 1
        else if (conn.effectiveType.includes('3g')) this.concurrency = 2
        else this.concurrency = 4
      }
    } catch (e) {console.log('Connection API not available', e)}
    axios.get(`/universities`).then(r => this.universities = r.data)
  },
  methods: {
    onUniChange() {
      this.selected_ceremony = null
      axios.get(`/universities/${this.selected_university.id}/ceremonies`).then(r => this.ceremonies = r.data)
    },
    formatCeremonyOption(c) {
      const formattedDate = new Date(c.datetime).toLocaleDateString('en-GB', {weekday: 'long', year:'numeric', month:'long', day:'numeric', hour:'numeric', minute:'2-digit', hour12: true})
      return c.label ? `${formattedDate} - ${c.label}` : formattedDate
    },
    onDrop(e) {
      const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith('image/'))
      this.addFiles(files)
    },
    onFileChange(e) {
      const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'))
      this.addFiles(files)
      e.target.value = ''
    },
    addFiles(fileList) {
      const startId = this.files.length
      const mapped = fileList.map((file, idx) => ({ id: startId + idx, file, status: 'queued', progress: 0 }))
      this.files = this.files.concat(mapped)
    },
    async startUpload() {
      if (!this.selected_ceremony || this.files.length === 0) return
      this.uploading = true
      try {
        // Queue uploads with limited concurrency; presign is obtained per-file (just-in-time)
        const queue = [...this.files]
        const workers = Array.from({ length: this.concurrency }).map(() => this.worker(queue))
        await Promise.all(workers)
      } catch (e) {
        console.error('Upload failed to start', e)
      } finally {
        this.uploading = false
      }
    },
    async worker(queue) {
      while (queue.length) {
        const item = queue.shift()
        if (!item) break
        await this.uploadOne(item)
      }
    },
    async uploadOne(item) {
      item.status = 'thumbnailing'
      let thumbBlob
      try {
        thumbBlob = await this.makeThumbnail(item.file, 1280)
      } catch (e) {
        console.warn('Thumbnail generation failed; falling back to original', e)
        thumbBlob = item.file
      }
      item.status = 'uploading'
      const totalBytes = item.file.size + (thumbBlob?.size||0)
      let uploaded = 0
      const update = () => { item.progress = Math.min(100, Math.floor((uploaded/Math.max(1,totalBytes))*100)) }

      const attemptUpload = async (attempt = 1) => {
        // Ensure we have fresh presigned URLs for this file
        const presign = await this.ensurePresign(item)
        const put = (url, blob, contentType) => axios.put(url, blob, {
          headers: { 'Content-Type': contentType },
          transformRequest: (data, headers) => { delete headers.common['Authorization']; return data },
          onUploadProgress: (e) => { uploaded += e.loaded; update() }
        })
        try {
          await Promise.all([
            put(presign.fullres.url, item.file, presign.fullres.content_type || 'application/octet-stream'),
            put(presign.thumbnail.url, thumbBlob, 'image/jpeg'),
          ])
          return true
        } catch (err) {
          const status = err?.response?.status
          // For 403 (expired signature) or network timeouts, retry with backoff and fresh presign
          if (attempt < this.maxRetries && (!status || status === 403 || status >= 500)) {
            const delay = Math.min(30000, 1000 * Math.pow(2, attempt))
            await new Promise(r => setTimeout(r, delay))
            return attemptUpload(attempt + 1)
          }
          throw err
        }
      }

      try {
        const ok = await attemptUpload(1)
        if (ok) {
          item.status = 'done'
          item.progress = 100
        }
      } catch (e) {
        console.error('Upload error', e)
        item.status = 'error'
      }
    },
    async ensurePresign(item) {
      if (item._presign && item._presign.filename === item.file.name) return item._presign
      const body = {
        ceremony_id: this.selected_ceremony,
        files: [{ filename: item.file.name, content_type: item.file.type, size: item.file.size }]
      }
      const r = await axios.post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/upload/presign`, body)
      const presign = r.data.items && r.data.items[0]
      if (!presign) throw new Error('Failed to obtain presign for file')
      item._presign = presign
      return presign
    },
    makeThumbnail(file, maxWidth) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const scale = Math.min(1, maxWidth / img.width)
          const w = Math.floor(img.width * scale)
          const h = Math.floor(img.height * scale)
          const canvas = document.createElement('canvas')
          canvas.width = w; canvas.height = h
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, w, h)
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.8)
        }
        img.onerror = (e) => reject(e)
        const reader = new FileReader()
        reader.onload = (e) => { img.src = e.target.result }
        reader.onerror = (e) => reject(e)
        reader.readAsDataURL(file)
      })
    },
  }
}
</script>

<style scoped>
</style>
